import React from 'react';
import { Card, CardContent, Typography, Avatar, Box, Grid } from '@mui/material';
import { Player } from '../types';
import { usePlayer } from '../contexts/PlayerContext';

interface PlayerCardProps {
  player: Player;
  onSelect?: (player: Player) => void;
  isSelected?: boolean;
}

const PlayerCard: React.FC<PlayerCardProps> = ({ player, onSelect, isSelected = false }) => {
  const { openPlayerDialog } = usePlayer();
  
  const handleClick = () => {
    if (onSelect) {
      onSelect(player);
    }
  };
  
  const handleNameClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking name
    openPlayerDialog(player);
  };

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { boxShadow: 3 },
        ...(isSelected && {
          border: '2px solid #3b82f6',
          backgroundColor: '#eff6ff'
        })
      }}
      onClick={handleClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: 'grey.300' }}>
            <Typography variant="h6" color="text.secondary">
              {player.name.split(' ').map(n => n[0]).join('')}
            </Typography>
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline', color: 'primary.main' }
              }}
              onClick={handleNameClick}
            >
              {player.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {player.position} • {player.team}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
              {player.fantasyPointsPerGame?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              FPTS
            </Typography>
          </Box>
        </Box>
        
        <Grid container spacing={1}>
          <Grid size={4} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {player.pointsPerGame?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PTS
            </Typography>
          </Grid>
          <Grid size={4} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {player.reboundsPerGame?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              REB
            </Typography>
          </Grid>
          <Grid size={4} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {player.assistsPerGame?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AST
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PlayerCard;
